import { useCallback, useState } from 'react';
import { Button, Progress, Stack, Tooltip } from '@mantine/core';
import { IconFileExport } from '@tabler/icons-react';
import type { TableColumn } from '@/components/ccms-components/data-display/types';

interface ExportCsvButtonProps<T> {
  /** Rows to export. Pass the filtered/sorted set (not the paginated slice) so the export matches
   *  what the filters/search produced, not just the current page. */
  data: T[];
  /** Same column defs used by the table, so the CSV headers/order can't drift from what's on screen.
   *  Columns with key 'select' or 'actions' are skipped automatically — pass the full array. */
  columns: TableColumn<T>[];
  /** Base filename, without extension or timestamp. */
  filename: string;
  disabled?: boolean;
  label?: string;
  /**
   * When provided, the button ignores `data` and instead calls this on click to fetch the full
   * set (used for server-paged sources where `data` only holds one page). Resolve with the rows
   * to export, or null to abort. Existing callers that omit this keep the old synchronous behaviour.
   */
  fetchAll?: () => Promise<T[] | null>;
  /** Optional progress feed while `fetchAll` runs. Renders a bar under the button. */
  progress?: { loaded: number; total: number } | null;
}

// Columns that only exist for UI chrome (checkboxes, row actions) and have nothing meaningful to
// put in a cell — excluded from export regardless of what's passed in.
const NON_EXPORT_KEYS = new Set(['select', 'actions']);

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  // Quote whenever the value contains the Excel delimiter, a quote, or newline.
  if (/[;"\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

export function ExportCsvButton<T>({
  data, columns, filename, disabled, label = 'Export CSV', fetchAll, progress,
}: ExportCsvButtonProps<T>) {
  const [busy, setBusy] = useState(false);

  const exportableColumns = columns.filter((c) => !NON_EXPORT_KEYS.has(c.key));

  const download = useCallback((rows: T[]) => {
    const headerRow = exportableColumns.map((c) => csvEscape(c.header || c.key));
    const bodyRows = rows.map((row) =>
      exportableColumns.map((c) => {
        // Use an explicit export accessor when a column needs a plain-text representation that
        // differs from its table value; otherwise use the column's own accessor.
        let value: unknown;
        if (c.exportAccessor) {
          value = c.exportAccessor(row);
        } else if (typeof c.accessor === 'function') {
          value = (c.accessor as (row: T) => unknown)(row);
        } else {
          value = (row as any)[c.accessor as keyof T];
        }
        return csvEscape(value);
      }),
    );

    // Semicolons match the list separator used by Excel in this environment.
    const csvContent = [headerRow, ...bodyRows].map((r) => r.join(';')).join('\r\n');
    // Leading BOM so Excel opens UTF-8 content (names, etc.) without mangling it.
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const stamp = new Date().toISOString().slice(0, 10);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [exportableColumns, filename]);

  const handleExport = useCallback(async () => {
    // Legacy path: no fetchAll → export whatever we were handed, synchronously.
    if (!fetchAll) {
      download(data);
      return;
    }
    // Server path: fetch the whole set first, then export.
    setBusy(true);
    try {
      const rows = await fetchAll();
      if (rows) download(rows);
    } catch (e) {
      console.error('[ExportCsvButton] fetchAll failed:', e);
    } finally {
      setBusy(false);
    }
  }, [fetchAll, data, download]);

  const pct = progress && progress.total > 0
    ? Math.round((progress.loaded / progress.total) * 100)
    : 0;

  const noRows = !fetchAll && data.length === 0;
  const rowCount = fetchAll ? undefined : data.length;

  return (
    <Stack gap={4} align="flex-end">
      <Tooltip
        label={
          busy
            ? 'Preparing export…'
            : noRows
              ? 'No rows to export'
              : rowCount !== undefined
                ? `Export ${rowCount} row${rowCount === 1 ? '' : 's'}`
                : 'Export all matching records'
        }
      >
        <Button
          variant="outline"
          color="green"
          leftSection={<IconFileExport size={16} />}
          onClick={handleExport}
          disabled={disabled || busy || noRows}
          loading={busy}
        >
          {label}
        </Button>
      </Tooltip>

      {fetchAll && busy && progress && (
        <Stack gap={2} w={220}>
          <Progress value={pct} animated size="sm" />
          <span style={{ fontSize: 11, color: 'var(--mantine-color-dimmed)' }}>
            {progress.loaded.toLocaleString()} / {progress.total.toLocaleString()}
          </span>
        </Stack>
      )}
    </Stack>
  );
}

export default ExportCsvButton;